<template>
  <div>
    <div
      class="bg-white rounded border-l-4 shadow ring-1 ring-black ring-opacity-5 divide-y"
      :class="{
        'border-gray-600': !product || product.type === 'package',
        'border-blue-500': product && product.type === 'basic',
        'border-yellow-500': product && product.type === 'service',
      }"
    >
      <div class="flex p-4 items-top gap-4">
        <div v-if="hasSubOrderItems && !specsVisible" class="flex-none">
          <button
            type="button"
            class="shadow-sm rounded-md relative inline-flex items-center border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            @click="visible ? hide() : show()"
          >
            <svg
              v-if="visible"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="w-5 h-5"
            >
              <path
                fill-rule="evenodd"
                d="M14.77 12.79a.75.75 0 01-1.06-.02L10 8.832 6.29 12.77a.75.75 0 11-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 01-.02 1.06z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="w-5 h-5"
            >
              <path
                fill-rule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
        <div class="flex-1">
          <div>
            <h3 class="text-sm font-medium rounded px-3 py-1.5 border border-gray-100 bg-gray-100">
              {{ orderItem.name }}
            </h3>
            <p class="text-xs p-2"><span>Status:</span> {{ orderItem.status || 'Not set' }}</p>
          </div>
          <BaseFormTextarea
            :value="requirements"
            v-if="specsVisible"
            rows="10"
            class="block mt-2"
            @input="$emit('update:requirements', $event)"
          />
        </div>
        <div v-if="!specsVisible" class="w-24 text-right border-l">
          <span class="font-medium">{{ price }}</span> &euro;
        </div>
      </div>
      <div v-if="!specsVisible" class="flex p-4 gap-4 justify-between items-center">
        <div class="flex gap-4 items-center">
          <span v-if="!lockEdit" class="isolate inline-flex rounded-md shadow-sm">
            <button
              type="button"
              class="first:rounded-l-md last:rounded-r-md relative inline-flex items-center border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              @click="addLine(orderItem)"
            >
              Add line
            </button>
            <button
              v-if="product && product.type === 'package'"
              type="button"
              class="first:rounded-l-md last:rounded-r-md relative -ml-px inline-flex items-center border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              @click="addProduct(orderItem)"
            >
              Add product
            </button>
            <button
              v-if="product && product.type === 'basic'"
              type="button"
              class="first:rounded-l-md last:rounded-r-md relative -ml-px inline-flex items-center border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              @click="addService(orderItem)"
            >
              Add service
            </button>
          </span>

          <span v-if="!lockEdit" class="isolate inline-flex rounded-md shadow-sm">
            <button
              type="button"
              class="first:rounded-l-md last:rounded-r-md relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              @click="edit(orderItem)"
            >
              Edit
            </button>
            <button
              v-if="orderItem.uuid"
              type="button"
              class="first:rounded-l-md last:rounded-r-md relative -ml-px inline-flex items-center border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              @click="editSpecs"
            >
              Edit specs
            </button>
            <button
              type="button"
              class="first:rounded-l-md last:rounded-r-md relative -ml-px inline-flex items-center border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              @click="remove(orderItem)"
            >
              Remove
            </button>
          </span>

          <span v-if="!lockEdit" class="isolate inline-flex rounded-md shadow-sm">
            <button
              v-if="producable && !hasProducable && orderItem.uuid && orderItem.projectId"
              type="button"
              class="first:rounded-l-md last:rounded-r-md relative -ml-px inline-flex items-center border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              @click="createProducable({ orderItem, product })"
            >
              Create {{ producable }}
            </button>
            <button
              v-if="producable && hasProducable"
              type="button"
              class="first:rounded-l-md last:rounded-r-md relative -ml-px inline-flex items-center border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              @click="editOrderItemProducable(orderItem)"
            >
              Edit {{ producable }}
            </button>
          </span>
        </div>
        <OrderItemProjectManager
          v-if="orderItem.uuid && !orderItem.parentOrderItemUuid && !lockEdit"
          :order-item="orderItem"
          :account-id="sellerAccountId"
          :customer-account-id="customerAccountId"
          :default-project-data="defaultProjectData"
          @change="handleProjectChange"
        />
      </div>
    </div>
    <div v-if="(specsVisible || visible) && hasSubOrderItems" class="pt-2">
      <div class="flex ml-10">
        <div class="flex-1">
          <BaseLayoutSpaced snug>
            <template v-for="(subOrderItem, subOrderItemIdx) in subOrderItems">
              <OrderItem
                ref="child"
                v-if="subOrderItem"
                :key="subOrderItemIdx"
                nested
                :default-project-data="defaultProjectData"
                :lock-edit="lockEdit"
                :seller-account-id="sellerAccountId"
                :customer-account-id="customerAccountId"
                :order-item="subOrderItem"
                :requirements.sync="subOrderItem.requirements"
                :order-items="orderItems"
                :specs-visible="specsVisible"
                @add-line="addLine"
                @add-product="addProduct"
                @add-service="addService"
                @create-producable="createProducable"
                @edit-producable="editProducable"
                @change="handleChange"
                @remove="remove"
              />
            </template>
          </BaseLayoutSpaced>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import OrderItemProjectManager from './OrderItemProjectManager.vue';

export default {
  name: 'OrderItem',
  components: { OrderItemProjectManager },
  props: {
    nested: Boolean,
    lockEdit: Boolean,
    sellerAccountId: {
      type: String,
      required: true,
    },
    customerAccountId: {
      type: String,
      required: true,
    },
    orderItem: {
      type: Object,
      required: true,
    },
    defaultProjectData: {
      type: Object,
      default: () => ({}),
    },
    requirements: {
      type: String,
      default: '',
    },
    orderItems: {
      type: Array,
      default: () => [],
    },
    specsVisible: Boolean,
  },
  data: () => ({
    visible: false,
  }),
  computed: {
    ...mapState('entities', {
      product(state) {
        if (!this.orderItem.orderableUuid || this.orderItem.orderableType !== 'product') {
          return null;
        }
        return state.products.byId[this.orderItem.orderableUuid] || null;
      },
    }),
    producable() {
      if (!this.product || !this.product.producesModelType) {
        return null;
      }
      const producables = {
        campaigns: 'Ad plan',
        pages: 'Page',
        media: 'Creative',
      };
      return producables[this.product.producesModelType] || null;
    },
    hasProducable() {
      return (
        typeof this.orderItem.producableType === 'string' &&
        typeof this.orderItem.producableId === 'string'
      );
    },
    subOrderItems() {
      return this.orderItems.slice(0).map((orderItem) => {
        if (this.orderItem.uuid && orderItem.parentOrderItemUuid === this.orderItem.uuid) {
          return orderItem;
        }
        if (this.orderItem.tempId && orderItem.tempParentId === this.orderItem.tempId) {
          return orderItem;
        }
        return null;
      });
    },
    hasSubOrderItems() {
      return this.subOrderItems.filter((x) => x).length > 0;
    },
    hasSpecs() {
      return (
        typeof this.orderItem.requirements === 'string' && this.orderItem.requirements.length > 0
      );
    },
    price() {
      return this.orderItem.quantity * this.orderItem.pricePerUnit;
    },
    hasPrice() {
      return this.price > 0;
    },
    hasAnyChildWithPrice() {
      return this.subOrderItems.filter((x) => x).some((x) => x.pricePerUnit > 0);
    },
    isBaseOrderItem() {
      const { parentOrderItemUuid = null, tempParentId = null } = this.orderItem || {};
      return parentOrderItemUuid === null && tempParentId === null;
    },
    exists() {
      return this.orderItem && typeof this.orderItem.uuid === 'string';
    },
  },
  methods: {
    show() {
      this.visible = true;
    },
    hide() {
      this.visible = false;
    },
    addLine(orderItem) {
      this.$emit('add-line', orderItem);
    },
    addProduct(orderItem) {
      this.$emit('add-product', orderItem);
    },
    addService(orderItem) {
      this.$emit('add-service', orderItem);
    },
    edit(orderItem) {
      this.$modal.show({
        component: () => import('./ModalOrderItemEdit.vue'),
        props: {
          orderItem: JSON.parse(JSON.stringify(orderItem)),
        },
        on: {
          update: this.handleChange,
        },
        hideEvent: 'closed',
      });
    },
    handleChange(orderItem) {
      this.$emit('change', orderItem);
    },
    editSpecs() {
      this.$modal.show({
        component: () => import('Components/OrderItems/OrderItemModalWithAside.vue'),
        props: {
          orderItemId: this.orderItem.uuid,
          accountId: this.sellerAccountId,
          clientId: this.customerAccountId,
        },
        hideEvent: 'closed',
      });
    },
    createProducable(payload) {
      this.$emit('create-producable', payload);
    },
    editOrderItemProducable(orderItem) {
      this.editProducable({
        producableType: orderItem.producableType,
        producableId: orderItem.producableId,
      });
    },
    editProducable(payload) {
      this.$emit('edit-producable', payload);
    },
    remove(orderItem) {
      this.$emit('remove', orderItem);
    },
    handleProjectChange(projectId) {
      this.handleChange({
        ...JSON.parse(JSON.stringify(this.orderItem)),
        projectId,
      });
    },
  },
};
</script>
